import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, Transition as _Transition } from "vue";
const _hoisted_1 = {
    key: 1,
    class: "rp-page rp-skeleton"
};
const _hoisted_2 = {
    key: 0,
    class: "rp-page"
};
const _hoisted_3 = { class: "rp-totals" };
const _hoisted_4 = { class: "rp-totals-row" };
const _hoisted_5 = { class: "rp-totals-amount" };
const _hoisted_6 = { class: "rp-totals-row" };
const _hoisted_7 = { class: "rp-totals-amount" };
const _hoisted_8 = { class: "rp-list" };
const _hoisted_9 = {
    key: 0,
    class: "rp-section-header"
};
const _hoisted_10 = {
    key: 1,
    class: "rp-section-header"
};
const _hoisted_11 = ["onClick"];
const _hoisted_12 = { class: "rp-avatar-wrap" };
const _hoisted_13 = ["src"];
const _hoisted_14 = ["src", "onError"];
const _hoisted_15 = {
    key: 2,
    class: "rp-paid-tick"
};
const _hoisted_16 = { class: "rp-info" };
const _hoisted_17 = { class: "rp-name" };
const _hoisted_18 = { class: "rp-avg" };
const _hoisted_19 = {
    key: 0,
    class: "rp-status rp-status--increasing"
};
const _hoisted_20 = {
    key: 1,
    class: "rp-status rp-status--decreasing"
};
const _hoisted_21 = {
    key: 2,
    class: "rp-status rp-status--stable"
};
const _hoisted_22 = { class: "rp-amount" };
const _hoisted_23 = { class: "rp-amount-value" };
const _hoisted_24 = ["src"];
const _hoisted_25 = { class: "rp-ai-card" };
const _hoisted_26 = { class: "drawer-tx-wrap" };
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useCustomer } from '@/composables/useCustomer';
import { useBanking, pendingBankingRevalidate } from '@/composables/useBanking';
import { useTransactionUpdateHandoff } from '@/composables/useTransactionUpdateHandoff';
import { inflightCategoryUpdate } from '@/components/elements/transactionUpdateState';
import { wellKnownLogosOnly } from '@/config';
import { getMerchantLogoUrl, getMerchantLogoStyle, onMerchantLogoError, isWellKnownLogosOnly, getCategoryIconUrl } from '@/utils/helpers';
import HeaderBlock from './elements/HeaderBlock.vue';
import SimpleDrawerPanel from './elements/SimpleDrawerPanel.vue';
import TransactionsList from './elements/TransactionsList.vue';
import AiChatBanner from './elements/AiChatBanner.vue';
import { snapToSpendingCycle } from './elements/spendingAnalysisFilterState';
export default /*@__PURE__*/ _defineComponent({
    __name: 'RecurringPaymentsPage',
    setup(__props) {
        const state = useAppState();
        const router = useRouter();
        useAuth();
        const { getCustomer } = useCustomer();
        const { getBankingInfo, getRecurringPayments } = useBanking();
        const { updatingTransactionId, takePending } = useTransactionUpdateHandoff();
        const recurringPayments = ref(null);
        const selectedMerchantName = ref(null);
        const selectedPayment = ref(null);
        const drawerOpen = ref(false);
        // Pre-filter recent transactions to the selected recurring payment's merchant
        // using exact identity (same predicate as findClassificationForMerchant).
        // Done here instead of via TransactionsList's free-text search, which would
        // substring-match the merchant name against amounts/categories/tags — fatal
        // for short or numeric names like "3", "EE", "O2".
        const drawerTransactions = computed(() => {
            const groups = state.bankInfo?.recentTransactions ?? [];
            const p = selectedPayment.value;
            if (!p)
                return [];
            const matches = (t) => t.merchantName === p.merchantName ||
                (p.merchant && t.merchant?.id === p.merchant.id) ||
                (p.merchant && t.merchant?.name === p.merchant.name);
            const out = [];
            for (const g of groups) {
                const filtered = g.filter(matches);
                if (filtered.length)
                    out.push(filtered);
            }
            return out;
        });
        function isPaid(p) {
            const today = new Date().getDate();
            const cycleDay = state.bankInfo?.spendingCycle?.day ?? 1;
            const cyclePos = (d) => (d - cycleDay + 31) % 31;
            return cyclePos(p.avgPaymentDay) < cyclePos(today);
        }
        const paidStartIndex = computed(() => {
            const payments = recurringPayments.value?.payments;
            if (!payments?.length)
                return -1;
            const idx = payments.findIndex((p) => isPaid(p));
            return idx;
        });
        const upcomingCount = computed(() => {
            const payments = recurringPayments.value?.payments;
            if (!payments?.length)
                return 0;
            const idx = paidStartIndex.value;
            return idx === -1 ? payments.length : idx;
        });
        const paidCount = computed(() => {
            const payments = recurringPayments.value?.payments;
            if (!payments?.length)
                return 0;
            const idx = paidStartIndex.value;
            return idx === -1 ? 0 : payments.length - idx;
        });
        function findClassificationForMerchant(p) {
            const groups = state.bankInfo?.recentTransactions ?? [];
            for (const group of groups) {
                const match = group.find((t) => t.merchantName === p.merchantName ||
                    (p.merchant && t.merchant?.id === p.merchant.id) ||
                    (p.merchant && t.merchant?.name === p.merchant.name));
                if (match?.effectiveClassification)
                    return match.effectiveClassification;
            }
            return null;
        }
        function isCategoryAvatar(p) {
            const input = makeLogoInput(p);
            return !!input.wellKnownLogosOnly && !input.wellKnown;
        }
        function makeLogoInput(p) {
            const classification = p.effectiveClassification ?? findClassificationForMerchant(p);
            return {
                name: p.merchant?.name ?? p.merchantName ?? "",
                wellKnown: !!p.merchant,
                iconPath: classification?.iconPath,
                iconColour: classification?.color,
                wellKnownLogosOnly: isWellKnownLogosOnly(wellKnownLogosOnly),
            };
        }
        function getBankAccount(id) {
            return state.bankInfo?.accounts.find((ba) => ba.id === id);
        }
        function goToTransaction(t) {
            router.push('/TransactionDetail/' + t.id);
        }
        function showPayments(p) {
            selectedPayment.value = p;
            selectedMerchantName.value = p.merchant?.name ?? p.merchantName;
            drawerOpen.value = true;
        }
        function onLoad() {
            getCustomer(() => {
                getBankingInfo(() => {
                    getRecurringPayments((res) => {
                        const cycleDay = state.bankInfo?.spendingCycle?.day ?? 1;
                        const cyclePos = (d) => (d - cycleDay + 31) % 31;
                        res.payments = res.payments.sort((a, b) => {
                            const aPaid = isPaid(a) ? 1 : 0;
                            const bPaid = isPaid(b) ? 1 : 0;
                            if (aPaid !== bPaid)
                                return aPaid - bPaid;
                            return snapToSpendingCycle.value
                                ? cyclePos(a.avgPaymentDay) - cyclePos(b.avgPaymentDay)
                                : a.avgPaymentDay - b.avgPaymentDay;
                        });
                        res.payments.forEach((rp) => {
                            rp.bankAccount = getBankAccount(rp.bankAccountId);
                        });
                        recurringPayments.value = res;
                        // Deepen state.bankInfo to 93 days of history so the merchant
                        // drawer's TransactionsList has enough history to filter against.
                        // SWR: serve cached on subsequent mounts, refresh in the
                        // background. The previous state.useCache flip-and-restore was
                        // dead code — useApi consults a separate localStorage toggle.
                        getBankingInfo(null, /* refreshCache */ false, /* daysHistory */ 93, /* staleWhileRevalidate */ true);
                    });
                });
            });
        }
        onMounted(() => {
            // Adopt any in-flight category update from TDP so the affected row shows a
            // spinner immediately. takePending falls back from the one-shot
            // pendingTransactionUpdateId to the long-lived inflightCategoryUpdate, so
            // it works on both the first landing here and any subsequent bounce-back.
            takePending();
            // If a write is still propagating, SWR+poll banking until the cached
            // payload reflects it. The dedup in getBankingInfo attaches this caller to
            // TDP's already-running poll instead of starting a parallel one — clearing
            // the row spinner here on the same !isStale callback that updates the list.
            const revalidate = pendingBankingRevalidate.value || !!inflightCategoryUpdate.value;
            pendingBankingRevalidate.value = false;
            if (revalidate) {
                getBankingInfo((isStale) => {
                    if (!isStale)
                        updatingTransactionId.value = null;
                }, false, null, /* staleWhileRevalidate */ true, /* pollUntilChanged */ true);
            }
            onLoad();
        });
        return (_ctx, _cache) => {
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            const _component_v_icon = _resolveComponent("v-icon");
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                (_unref(state).customer)
                    ? (_openBlock(), _createBlock(HeaderBlock, {
                        key: 0,
                        title: "Recurring payments",
                        bgcolor: "white"
                    }))
                    : _createCommentVNode("", true),
                (!recurringPayments.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                        _createVNode(_component_v_skeleton_loader, {
                            type: "card",
                            height: "96",
                            class: "rp-skel-totals"
                        }),
                        _createVNode(_component_v_skeleton_loader, {
                            type: "list-item-avatar-two-line, list-item-avatar-two-line, list-item-avatar-two-line, list-item-avatar-two-line",
                            class: "rp-skel-card"
                        })
                    ]))
                    : _createCommentVNode("", true),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        (recurringPayments.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                                _createElementVNode("div", _hoisted_3, [
                                    _createElementVNode("div", _hoisted_4, [
                                        _cache[1] || (_cache[1] = _createElementVNode("span", { class: "rp-totals-label" }, "Monthly total", -1)),
                                        _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(state).formatter.format(Math.abs(Math.round(recurringPayments.value.total)))), 1)
                                    ]),
                                    _createElementVNode("div", _hoisted_6, [
                                        _cache[2] || (_cache[2] = _createElementVNode("span", { class: "rp-totals-label" }, "Remaining this month", -1)),
                                        _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(state).formatter.format(Math.abs(Math.round(recurringPayments.value.leftToPay)))), 1)
                                    ])
                                ]),
                                _createElementVNode("div", _hoisted_8, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recurringPayments.value.payments, (p, i) => {
                                        return (_openBlock(), _createElementBlock(_Fragment, {
                                            key: p.id
                                        }, [
                                            (i === 0 && upcomingCount.value > 0)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                                    _cache[3] || (_cache[3] = _createElementVNode("span", null, "Coming up", -1)),
                                                    _createElementVNode("span", null, _toDisplayString(upcomingCount.value), 1)
                                                ]))
                                                : _createCommentVNode("", true),
                                            (i === paidStartIndex.value)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                                    _cache[4] || (_cache[4] = _createElementVNode("span", null, "Already paid", -1)),
                                                    _createElementVNode("span", null, _toDisplayString(paidCount.value), 1)
                                                ]))
                                                : _createCommentVNode("", true),
                                            _createElementVNode("div", {
                                                class: _normalizeClass(["rp-card", { 'rp-card--paid': isPaid(p) }]),
                                                onClick: ($event) => (showPayments(p))
                                            }, [
                                                _createElementVNode("div", _hoisted_12, [
                                                    (isCategoryAvatar(p))
                                                        ? (_openBlock(), _createElementBlock("div", {
                                                            key: 0,
                                                            class: "rp-avatar rp-avatar--category",
                                                            style: _normalizeStyle(_unref(getMerchantLogoStyle)(makeLogoInput(p)))
                                                        }, [
                                                            _createElementVNode("img", {
                                                                class: "rp-avatar-category-img",
                                                                src: _unref(getCategoryIconUrl)(makeLogoInput(p).iconPath),
                                                                alt: ""
                                                            }, null, 8, _hoisted_13)
                                                        ], 4))
                                                        : (_openBlock(), _createElementBlock("img", {
                                                            key: 1,
                                                            class: "rp-avatar",
                                                            src: _unref(getMerchantLogoUrl)(makeLogoInput(p)),
                                                            onError: ($event) => (_unref(onMerchantLogoError)($event, makeLogoInput(p)))
                                                        }, null, 40, _hoisted_14)),
                                                    (isPaid(p))
                                                        ? (_openBlock(), _createElementBlock("span", _hoisted_15, [
                                                            _createVNode(_component_v_icon, {
                                                                size: "10",
                                                                color: "white"
                                                            }, {
                                                                default: _withCtx(() => _cache[5] || (_cache[5] = [
                                                                    _createTextVNode("mdi-check")
                                                                ])),
                                                                _: 1
                                                            })
                                                        ]))
                                                        : _createCommentVNode("", true)
                                                ]),
                                                _createElementVNode("div", _hoisted_16, [
                                                    _createElementVNode("span", _hoisted_17, _toDisplayString(p.merchant ? p.merchant.name : p.merchantName), 1),
                                                    _createElementVNode("span", _hoisted_18, "Avg. payment day: " + _toDisplayString(p.avgPaymentDay), 1),
                                                    (p.increasing)
                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                                            _createVNode(_component_v_icon, {
                                                                size: "10",
                                                                color: "#FF7E7E"
                                                            }, {
                                                                default: _withCtx(() => _cache[6] || (_cache[6] = [
                                                                    _createTextVNode("mdi-arrow-up")
                                                                ])),
                                                                _: 1
                                                            }),
                                                            _cache[7] || (_cache[7] = _createElementVNode("span", null, "Increasing", -1))
                                                        ]))
                                                        : (p.changing && !p.increasing)
                                                            ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                                                                _createVNode(_component_v_icon, {
                                                                    size: "10",
                                                                    color: "#6ED565"
                                                                }, {
                                                                    default: _withCtx(() => _cache[8] || (_cache[8] = [
                                                                        _createTextVNode("mdi-arrow-down")
                                                                    ])),
                                                                    _: 1
                                                                }),
                                                                _cache[9] || (_cache[9] = _createElementVNode("span", null, "Decreasing", -1))
                                                            ]))
                                                            : (_openBlock(), _createElementBlock("div", _hoisted_21, [
                                                                _createVNode(_component_v_icon, {
                                                                    size: "10",
                                                                    color: "rgba(65,54,85,0.5)"
                                                                }, {
                                                                    default: _withCtx(() => _cache[10] || (_cache[10] = [
                                                                        _createTextVNode("mdi-check")
                                                                    ])),
                                                                    _: 1
                                                                }),
                                                                _cache[11] || (_cache[11] = _createElementVNode("span", null, "Stable", -1))
                                                            ]))
                                                ]),
                                                _createElementVNode("div", _hoisted_22, [
                                                    _cache[12] || (_cache[12] = _createElementVNode("span", { class: "rp-amount-label" }, "LAST AMOUNT", -1)),
                                                    _createElementVNode("span", _hoisted_23, _toDisplayString(_unref(state).formatter.format(Math.abs(p.lastAmount))), 1)
                                                ]),
                                                _createElementVNode("img", {
                                                    class: "rp-arrow",
                                                    src: require('@/assets/arrow-right.png'),
                                                    alt: ""
                                                }, null, 8, _hoisted_24)
                                            ], 10, _hoisted_11)
                                        ], 64));
                                    }), 128))
                                ]),
                                _createElementVNode("div", _hoisted_25, [
                                    _cache[13] || (_cache[13] = _createElementVNode("div", { class: "rp-ai-text" }, [
                                        _createElementVNode("span", { class: "rp-ai-title" }, "Your assistant is here"),
                                        _createElementVNode("p", { class: "rp-ai-desc" }, " Regularly reviewing your recurring payments helps you identify subscriptions you no longer use. "),
                                        _createElementVNode("p", { class: "rp-ai-desc" }, " Ask your assistant to help you identify savings. ")
                                    ], -1)),
                                    _createVNode(AiChatBanner)
                                ]),
                                _createVNode(SimpleDrawerPanel, {
                                    modelValue: drawerOpen.value,
                                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((drawerOpen).value = $event)),
                                    title: selectedMerchantName.value || 'Recent Transactions',
                                    height: "90vh"
                                }, {
                                    default: _withCtx(() => [
                                        _createElementVNode("div", _hoisted_26, [
                                            (_unref(state).bankInfo)
                                                ? (_openBlock(), _createBlock(TransactionsList, {
                                                    key: 0,
                                                    search: false,
                                                    transactions: drawerTransactions.value,
                                                    formatter: _unref(state).formatter,
                                                    today: _unref(state).today,
                                                    yesterday: _unref(state).yesterday,
                                                    updatingTransactionId: _unref(updatingTransactionId),
                                                    categoryPills: true,
                                                    onTransactionClick: goToTransaction
                                                }, null, 8, ["transactions", "formatter", "today", "yesterday", "updatingTransactionId"]))
                                                : _createCommentVNode("", true)
                                        ])
                                    ]),
                                    _: 1
                                }, 8, ["modelValue", "title"])
                            ]))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                })
            ], 64));
        };
    }
});
